import axios from 'axios';
import LogRocket from 'logrocket';
import { ERROR_404 } from 'adminConstants/routes';
import { history } from '@redux/store';
import { DirtyAuthCacheChangeType } from 'further-types/admin-user';
import {
  GetTokenSilentlyOptions,
  RedirectLoginOptions,
} from '@auth0/auth0-react';

const baseURL = `${process.env.REACT_APP_API_URL}/v1/admin/`;
const awsURL = `${process.env.REACT_APP_API_URL}/v1/aws`;

export const api = axios.create({
  baseURL,
});

export const setupAxiosInstance = (
  getToken: (options?: GetTokenSilentlyOptions) => Promise<string>,
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>,
) => {
  api.interceptors.request.use(
    async (axiosConfig) => {
      const organization = localStorage.getItem('organizationId');
      let token;

      try {
        token = await getToken({
          authorizationParams: organization ? { organization } : undefined,
        });
      } catch (error) {
        // The point of this is for the case where a user is currently in Org A and gets removed from it, and their access_token
        // has expired. There will be a call to get a new token (getToken above) but will throw an error because the user is no
        // longer a member of Org A, so then we need to redirect the user through the auth0 login process where they can
        // choose a new org. There's no auth0 native way to handle this right now, so we need to do it manually.
        localStorage.removeItem('organizationId');
        loginWithRedirect({
          authorizationParams: { organization: undefined },
          appState: { returnTo: '/' },
        });
        return Promise.reject(error);
      }

      const { url } = axiosConfig;
      // don't add any additional headers if the url being called is not the further API.
      // e.g. it could be a call to the S3 bucket to fetch a file
      if (
        url?.startsWith('http') &&
        !url.startsWith(baseURL) &&
        !url.startsWith(awsURL)
      )
        return axiosConfig;

      if (token) {
        axiosConfig.headers.Authorization = `Bearer ${token}`;
      }

      if (LogRocket.sessionURL) {
        axiosConfig.headers['x-logrocket-url'] = LogRocket.sessionURL;
      }

      return axiosConfig;
    },
    (error) => {
      Promise.reject(error);
    },
  );

  api.interceptors.response.use(
    (response) => {
      // Skip version check for file downloads
      if (
        response.headers['content-type']?.includes(
          'application/octet-stream',
        ) ||
        response.headers['content-type']?.includes('application/pdf') ||
        response.headers['content-disposition']?.includes('attachment')
      ) {
        return response;
      }

      return response;
    },
    (error) => {
      const { forceLogout, authChangeType } = error?.response?.data || {};
      if (forceLogout) {
        if (authChangeType === DirtyAuthCacheChangeType.OrgRemoval) {
          localStorage.removeItem('organizationId');
        }
        const currentPath = `${history.location.pathname}${history.location.search}`;
        history.replace(`${currentPath}#refresh`);
      } else if (error?.response?.status === 401) {
        history.push(ERROR_404);
      }

      return Promise.reject(error);
    },
  );
};
